/*
 * Theme-agnostic exam hero behaviour.
 *
 * No imports, by design. This module is loaded by a bare <script type="module">
 * from the block, and the host theme is not guaranteed to publish an importmap
 * (Enterprise does not). A single unresolved bare specifier would fail the whole
 * module at parse time and neither custom element would be defined, so this
 * file stays dependency-free and signals its own change with a DOM CustomEvent.
 *
 * Registers two elements that live as siblings inside `.exam-hero__variants`:
 *   <exam-language-picker> owns the radio group
 *   <exam-hero-actions>     re-points the download / start-exam buttons
 * They coordinate through the radio group's native `change` event, which
 * bubbles to their shared container.
 *
 * The selection is a language, not a variant: the exam products carry a single
 * variant and the per-language rows live in the `certification` metaobject. A
 * language is addressed by its metaobject handle (`kbfc-spanish`), which is
 * stable across edits to the data and needs no numeric id to round-trip.
 */

/** The query param that remembers the chosen language across a reload. */
const LANGUAGE_PARAM = 'language';

const ROOT_SELECTOR = '.exam-hero__variants';
const INPUT_SELECTOR = 'input[data-exam-key]';

/** Fired on `.exam-hero__variants` after the selection changes. */
const CHANGE_EVENT = 'exam:languageSelected';

/** @returns {HTMLElement} the wrapper both elements coordinate through */
function sharedRoot(el) {
  return /** @type {HTMLElement} */ (el.closest(ROOT_SELECTOR) ?? el);
}

/** @returns {HTMLInputElement | null} */
function checkedInput(root) {
  return /** @type {HTMLInputElement | null} */ (root.querySelector(`${INPUT_SELECTOR}:checked`));
}

/**
 * The pill the page should open on: the one named by `?language=`, else the one
 * the server marked checked.
 *
 * Both elements below resolve their first paint through this, because they
 * upgrade independently and neither can assume it runs second. The picker then
 * also sets `.checked` so the native radio state agrees with the visual one.
 *
 * @param {HTMLElement} root
 * @returns {HTMLInputElement | null}
 */
function preferredInput(root) {
  const requested = languageFromUrl();
  if (requested) {
    const match = root.querySelector(`${INPUT_SELECTOR}[data-exam-key="${CSS.escape(requested)}"]`);
    if (match instanceof HTMLInputElement) return match;
  }
  return checkedInput(root);
}

/** @returns {string} the language currently named by the URL, or '' */
function languageFromUrl() {
  try {
    return new URL(window.location.href).searchParams.get(LANGUAGE_PARAM) ?? '';
  } catch (error) {
    return '';
  }
}

/* ------------------------------------------------------------------ *
 * <exam-language-picker>
 * ------------------------------------------------------------------ */
class ExamLanguagePicker extends HTMLElement {
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches(INPUT_SELECTOR)) {
      this.#apply(input);
    }
  };

  connectedCallback() {
    this.root = sharedRoot(this);
    this.root.addEventListener('change', this.#onChange);

    const initial = preferredInput(this.root);
    if (initial) {
      // Keep the native radio state in step with the visual selection. The
      // actions element below resolves its own first paint from `:checked`, and
      // assistive tech announces whichever radio is checked. Assigning `.checked`
      // does not fire `change`, so this does not re-enter #apply.
      if (!initial.checked) initial.checked = true;
      this.#apply(initial, { silent: true });
    }
  }

  disconnectedCallback() {
    this.root?.removeEventListener('change', this.#onChange);
  }

  /**
   * @param {HTMLInputElement} input
   * @param {{silent?: boolean}} [options] skip the URL write on first paint
   */
  #apply(input, options = {}) {
    const key = input.dataset.examKey;
    if (!key) return;

    for (const el of this.root.querySelectorAll('[data-exam-key-host]')) {
      el.dataset.currentChecked = String(el.dataset.examKeyHost === key);
    }

    if (!options.silent) this.#updateUrl(key);

    this.root.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        bubbles: true,
        detail: { handle: key, value: input.value },
      })
    );
  }

  #updateUrl(key) {
    if (!key) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(LANGUAGE_PARAM, key);
      window.history.replaceState({}, '', url);
    } catch (error) {
      /* URL rewriting is cosmetic; never let it break the selection. */
    }
  }
}

/* ------------------------------------------------------------------ *
 * <exam-hero-actions>
 * ------------------------------------------------------------------ */
class ExamHeroActions extends HTMLElement {
  #examMap = {};
  #onChange = () => {
    const input = checkedInput(this.root);
    if (input?.dataset.examKey) this.#apply(input.dataset.examKey);
  };

  connectedCallback() {
    this.root = sharedRoot(this);
    this.download = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('[data-exam-hero-download]')
    );
    this.link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-exam-hero-link]'));

    this.#parseMap();
    this.root.addEventListener('change', this.#onChange);

    // Same resolution the picker uses: this element may well be the one that
    // connects first, so it cannot read the picker's decision off the DOM and
    // has to reach the same answer on its own.
    const initial = preferredInput(this.root);
    if (initial?.dataset.examKey) this.#apply(initial.dataset.examKey);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('change', this.#onChange);
  }

  #parseMap() {
    const raw = this.dataset.exams;
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      for (const entry of parsed) this.#examMap[entry.handle] = entry;
    } catch (error) {
      console.warn('[exam-hero-actions] could not parse exams map:', error);
    }
  }

  /** @param {string} handle */
  #apply(handle) {
    const data = this.#examMap[handle];
    if (!data) return;

    this.#setTarget(this.download, data.study_material);
    this.#setTarget(this.link, data.link);
  }

  /**
   * Points an anchor at `href`, or turns it off.
   *
   * Disabled means the `href` attribute is *removed*, not set to `#`. An anchor
   * without `href` is not a link: it leaves the tab order and has no activation
   * behaviour, so it is a genuinely inert control rather than a link to nowhere.
   * The button stays visible either way -- the state is the message.
   *
   * @param {HTMLAnchorElement | null} anchor
   * @param {string} href
   */
  #setTarget(anchor, href) {
    if (!anchor) return;
    if (href) {
      anchor.setAttribute('href', href);
      anchor.removeAttribute('aria-disabled');
      anchor.removeAttribute('data-disabled');
    } else {
      anchor.removeAttribute('href');
      anchor.setAttribute('aria-disabled', 'true');
      anchor.setAttribute('data-disabled', 'true');
    }
  }
}

if (!customElements.get('exam-language-picker')) {
  customElements.define('exam-language-picker', ExamLanguagePicker);
}
if (!customElements.get('exam-hero-actions')) {
  customElements.define('exam-hero-actions', ExamHeroActions);
}